// The five recipes of "What people build": name, one-liner, the surfaces they compose, and a proof under the stage.
type Proof = {
  kind: 'case' | 'docs' | 'blog'
  title: string
  blurb: string
  cta: string
  href: string
  logo?: 'handshake' | 'bamboohr' | 'cribl'
}
/** What the stage shows for a build: an inline SVG loop, a muted video, or a still. All three sit in the same
 *  1200 x 750 frame; the section holds a loop for its length, a still for a shorter beat. */
export type Scene =
  | {
      kind: 'svg'
      name: 'agent-loop' | 'bulk-users' | 'ci-cd' | 'data-products' | 'embedded'
    }
  | { kind: 'video'; src: string; poster?: string }
  | { kind: 'image'; src: string }
/** Where each pill points: the docs page for the surface (omniframes' docs site is on GitHub Pages). */
export const TAG_LINKS: Record<string, string> = {
  'Model IDE': 'https://docs.omni.co/modeling/develop/guides/model-ide',
  CLI: 'https://docs.omni.co/developers/cli',
  'Agent skills': 'https://docs.omni.co/developers/agent-skills',
  Git: 'https://docs.omni.co/integrations/git',
  dbt: 'https://docs.omni.co/integrations/dbt',
  'REST API': 'https://docs.omni.co/api',
  'MCP server': 'https://docs.omni.co/ai/mcp',
  omniframes: 'https://exploreomni.github.io/omniframes/',
  Embed: 'https://docs.omni.co/embed',
}
export type Build = {
  id: string
  label: string
  line: string
  tags: string[]
  scene?: Scene
  proof: Proof
}

export const BUILDS: Build[] = [
  {
    id: 'agent-loop',
    label: 'Agent-driven modeling loop',
    line: 'In Omni or your coding agent, branch the model, fix validation errors, and open a PR.',
    tags: ['Model IDE', 'CLI', 'Agent skills', 'Git'],
    scene: { kind: 'svg', name: 'agent-loop' },
    proof: {
      kind: 'case',
      title: 'Case study',
      blurb:
        "Handshake migrated a 10 year analytics estate to Omni in 8 weeks with one engineer and AI. A coding agent ran Omni's validator and fixed the errors on a loop until the model was clean, while a skill migrated dashboards and other content.",
      cta: 'Read case study',
      href: 'https://omni.co/blog/case-study-handshake',
      logo: 'handshake',
    },
  },
  {
    id: 'ci-cd',
    label: 'CI/CD on model changes',
    line: 'Handle upstream changes automatically. Run validation on pull requests, and refresh the schema on merge.',
    tags: ['CLI', 'Git', 'dbt', 'REST API'],
    scene: { kind: 'svg', name: 'ci-cd' },
    proof: {
      kind: 'case',
      title: 'Case study',
      blurb:
        'Every dbt pull request at Cribl updates the model YAML through CI, pushes the branch, and refreshes Omni. One pipeline keeps 100 plus models documented.',
      cta: 'Read case study',
      href: 'https://omni.co/blog/case-study-cribl',
      logo: 'cribl',
    },
  },
  {
    id: 'bulk-users',
    label: 'Bulk user management',
    line: 'List users, update roles, modify permissions, and more from the terminal, a coding agent, or a script.',
    tags: ['REST API', 'CLI'],
    scene: { kind: 'svg', name: 'bulk-users' },
    proof: {
      kind: 'blog',
      title: 'Blog',
      blurb:
        "In the UI, this would require clicking through individual user profiles. With the CLI, an agent handles the batch, and every action still runs through Omni's permission system.",
      cta: 'Read the post',
      href: 'https://omni.co/blog/introducing-the-omni-cli',
    },
  },
  {
    id: 'data-products',
    label: 'Data products outside Omni',
    line: 'Pull governed data into notebooks, pipelines, and your own apps through the MCP server, CLI, and API.',
    tags: ['MCP server', 'CLI', 'REST API', 'omniframes'],
    scene: { kind: 'svg', name: 'data-products' },
    proof: {
      kind: 'docs',
      title: 'Docs',
      blurb:
        'Connect any MCP client to the semantic model. Every answer resolves through the same definitions and permissions.',
      cta: 'Read the docs',
      href: 'https://docs.omni.co/ai/mcp',
    },
  },
  {
    id: 'embedded',
    label: 'Embedded analytics in your product',
    line: "Build interactive data applications and AI chat for your customers, white-labeled and configured with ease on Omni's infrastructure.",
    tags: ['Embed', 'REST API'],
    scene: { kind: 'svg', name: 'embedded' },
    proof: {
      kind: 'case',
      title: 'Case study',
      blurb:
        'BambooHR launched embedded for more than 30,000 users in four months.',
      cta: 'Read case study',
      href: 'https://omni.co/blog/case-study-bamboohr',
      logo: 'bamboohr',
    },
  },
]
